import type { SeoScoreContext } from './context';
import type { SeoBlock } from './html-extract';
import { normalizeFr } from './tokenize-fr';

/**
 * Repères de découpage partagés par plusieurs règles : début de la FAQ, premier intertitre,
 * sections du corps. Un seul endroit pour décider ce qu'est « la FAQ » ou « l'introduction ».
 */

/** Index (dans `textBlocks`) du titre FAQ / questions ; +∞ s'il n'y en a pas. */
export function faqStartIndex(ctx: SeoScoreContext): number {
  const idx = ctx.textBlocks.findIndex(
    (b): boolean => b.type === 'heading' && /\b(faq|questions? frequentes?|questions?)\b/.test(normalizeFr(b.text)),
  );
  return idx === -1 ? Number.POSITIVE_INFINITY : idx;
}

/** Index (dans `textBlocks`) du premier intertitre H2+ ; -1 s'il n'y en a pas. */
export function firstHeadingIndex(ctx: SeoScoreContext): number {
  return ctx.textBlocks.findIndex((b): boolean => b.type === 'heading' && (b.level ?? 0) >= 2);
}

/** Paragraphes de l'introduction : avant le premier intertitre. */
export function introParagraphs(ctx: SeoScoreContext): SeoBlock[] {
  const first = firstHeadingIndex(ctx);
  return (first === -1 ? ctx.textBlocks : ctx.textBlocks.slice(0, first)).filter(
    (b): boolean => b.type === 'paragraph',
  );
}

export interface SeoBodySection {
  heading: SeoBlock;
  /** Blocs de texte de la section (H3 et leur contenu inclus), hors titres. */
  blocks: SeoBlock[];
}

/** Sections H2 du corps, avant la FAQ, avec leurs blocs (titres H3 exclus, contenu inclus). */
export function bodySections(ctx: SeoScoreContext): SeoBodySection[] {
  const faqStart = faqStartIndex(ctx);
  const out: SeoBodySection[] = [];
  let current: SeoBodySection | undefined;
  for (let i = 0; i < ctx.textBlocks.length && i < faqStart; i++) {
    const b = ctx.textBlocks[i];
    if (b.type === 'heading') {
      if (b.level === 2) {
        current = { heading: b, blocks: [] };
        out.push(current);
      }
      continue;
    }
    if (current) current.blocks.push(b);
  }
  return out;
}
